import React, { useState, useEffect } from 'react';
import { Star, Send, User, MessageSquare, Loader2, Quote, ArrowRight, ChevronDown, CheckCircle2, Globe, AlertCircle } from 'lucide-react';
import { dbService } from '../services/dbService';
import { Review } from '../types';

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [rating, setRating] = useState(5);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [showAll, setShowAll] = useState(false);

  const loadReviews = async () => {
    setIsLoading(true);
    const data = await dbService.fetchReviews();
    setReviews(data);
    setIsLoading(false);
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !comment || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const success = await dbService.submitReview({ name, rating, comment });
      
      if (success) {
        setName('');
        setComment('');
        setRating(5);
        setStatus('success');
        await loadReviews();
        setTimeout(() => setStatus('idle'), 6000);
      } else {
        setStatus('error');
        setErrorMessage('We could not post your review to the guestbook.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('A connection error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayedReviews = showAll ? reviews : reviews.slice(0, 5);

  return (
    <section id="reviews" className="relative py-24 bg-white overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6 relative z-10">
        
        <div className="flex flex-col lg:flex-row items-start justify-between mb-16 gap-12 lg:gap-32">
          <div className="max-w-2xl">
            <span className="text-sky-500 font-bold tracking-luxury uppercase text-[9px] mb-6 block">Guestbook</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-none font-serif tracking-tighter">
              Visitor <span className="italic font-light text-sky-500 block mt-3">Stories</span>
            </h2>
            <p className="mt-6 text-slate-500 text-lg font-light leading-relaxed">
              Read about the experiences of fellow travelers who have explored the island.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          
          <div className="lg:col-span-7">
            {isLoading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-10 h-10 text-sky-200 animate-spin" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-left py-12 border-t border-slate-50">
                <p className="text-slate-300 font-serif italic text-2xl font-light">Be the first to share your story...</p>
              </div>
            ) : (
              <div className="space-y-12 divide-y divide-slate-50">
                {displayedReviews.map((review, idx) => (
                  <div key={review.id} className={`group relative transition-all duration-700 ${idx !== 0 ? 'pt-12' : ''} animate-in fade-in duration-500`}>
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center font-serif text-lg italic group-hover:bg-sky-500 group-hover:text-white transition-all duration-500 border border-slate-100/50">
                          {review.name[0]}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-900 mb-0.5 tracking-tight">{review.name}</h4>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-2 h-2 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-100'}`} />
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{new Date(review.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="relative">
                      <p className="text-slate-600 text-base md:text-lg font-normal leading-relaxed pl-1 max-w-2xl">
                        "{review.comment}"
                      </p>
                    </div>
                  </div>
                ))}

                {!showAll && reviews.length > 5 && (
                  <div className="pt-12 flex justify-start">
                    <button 
                      onClick={() => setShowAll(true)}
                      className="group flex items-center gap-3 text-[10px] font-bold uppercase tracking-luxury text-sky-500 hover:text-slate-900 transition-all"
                    >
                      <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                      View More Stories
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-32 pt-2">
              <div className="mb-10 flex items-center justify-between">
                <div>
                  <span className="text-sky-500 font-bold tracking-luxury uppercase text-[8px] mb-3 block">Share Yours</span>
                  <h3 className="text-2xl font-bold text-slate-900 font-serif italic tracking-tight">Add Your Entry</h3>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                  <Globe className="w-3 h-3" /> Public
                </div>
              </div>

              {status === 'success' ? (
                <div className="bg-sky-50 p-10 rounded-[2.5rem] border border-sky-100 text-center animate-in zoom-in-95 duration-500">
                  <div className="w-14 h-14 bg-sky-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-sky-500/20">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-xl font-serif font-bold text-slate-900 mb-3 italic">Thank You</h4>
                  <p className="text-slate-500 text-sm leading-relaxed mb-8">
                    Your story has been added to our guestbook.
                  </p>
                  <button onClick={() => setStatus('idle')} className="text-sky-500 font-bold text-[9px] uppercase tracking-luxury hover:text-slate-900 transition-colors">Write another one</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-10">
                  {status === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-xs font-bold animate-in fade-in">
                      <AlertCircle className="w-4 h-4" />
                      {errorMessage}
                    </div>
                  )}

                  <div className="space-y-3">
                    <p className="text-[8px] font-bold uppercase tracking-luxury text-slate-400">Your Name</p>
                    <input 
                      required 
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="w-full bg-transparent border-b border-slate-100 py-3 text-lg focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-200 text-slate-800 font-light tracking-tight"
                      placeholder="e.g. Maria Clara"
                    />
                  </div>
                  <div className="space-y-3">
                    <p className="text-[8px] font-bold uppercase tracking-luxury text-slate-400">Rating</p>
                    <div className="flex gap-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button 
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className={`transition-all transform hover:scale-110 ${rating >= star ? 'text-amber-400' : 'text-slate-100'}`}
                        >
                          <Star className={`w-6 h-6 ${rating >= star ? 'fill-current' : ''}`} strokeWidth={1} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-[8px] font-bold uppercase tracking-luxury text-slate-400">Your Experience</p>
                    <textarea 
                      required 
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      className="w-full bg-transparent border-b border-slate-100 py-3 text-lg focus:outline-none focus:border-sky-500 transition-all h-24 resize-none placeholder:text-slate-200 text-slate-800 font-sans tracking-tight"
                      placeholder="Tell us about your trip..."
                    />
                  </div>
                  <button 
                    disabled={isSubmitting}
                    className="group w-full bg-sky-500 text-white py-5 rounded-xl font-bold transition-all hover:bg-slate-950 active:scale-95 disabled:opacity-50 text-[9px] uppercase tracking-luxury flex items-center justify-center gap-3 shadow-lg"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Post Review <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;